import { useState } from "react";
import styled from "styled-components";
import CTAFold, { CTAFoldContent, CTAFoldCTA } from "manifest-components/src/Containers/CTAFold";
import { ColorVariables, ColorClassNames } from "manifest-components/src/Colors";
import { Container, FlexContainer, Spacing } from "manifest-components/src/Containers";
import { Input } from "manifest-components/src/Inputs/Input";
import { validatePhoneNumber } from "manifest-components/src/utils/phone";
import { RadioTile } from "manifest-components/src/Tiles/RadioTile";
import Image from "manifest-components/src/Containers/Image";
import { Button, ButtonLink } from "manifest-components/src/Button";
import { Popup } from "manifest-components/src/Popup";
import { createFieldValidations, FieldValidationsType } from "manifest-components/src/Form/Form";
import logo from "manifest-components/src/Images/illustrations/Employer-Plan.png";
import ConfirmEmployerController, { EmployerOptionsProps, EmploymentStatus } from "./ConfirmEmployerController";
import API from "../../../utils/api";
import { route, RoutePaths, useTypedNavigate } from "../../../routes/RoutePaths";
import PEHPPreAccountCreationScreen from "./PEHPPreAccountCreationScreen";

const Line = styled(Container)`
  width: 100px;
  border-top: solid 3px ${ColorVariables.mint5};
  margin: 24px 0px;
`;

const EmployerOptions = (props: EmployerOptionsProps) => {
  const { employerName, handlePopup, handleContinue, showPopup, employmentStatus, handleClick } =
    ConfirmEmployerController(props);
  return (
    <CTAFold>
      <CTAFoldContent>
        <Popup
          content={
            <p className="small">
              Manifest is working hard to bring consolidation to everyone! Share your current employment information, so
              we can determine how to proceed.
            </p>
          }
          handlePopup={handlePopup}
          header="Why do we need this?"
          showContactInfo={false}
          showPopup={showPopup}
        />

        <Container className="header-section-a">
          <h4 className={ColorClassNames.coco}>
            First things first, tell us about your employment with
            <span>{` ${employerName}`}</span>
          </h4>
          <ButtonLink onClick={handlePopup}>
            <p className={`medium bold ${ColorClassNames.mint}`}>More info</p>
          </ButtonLink>
        </Container>

        <Container className="tile-section-a">
          <FlexContainer $flexDirection="column" $alignItems="center" className="tile-section-a">
            <RadioTile
              title={`I work at ${employerName}`}
              selected={employmentStatus === EmploymentStatus.currentlyWorks}
              onClick={handleClick(EmploymentStatus.currentlyWorks)}
            />
            <RadioTile
              title={`I used to work at ${employerName}`}
              selected={employmentStatus === EmploymentStatus.previouslyWorked}
              onClick={handleClick(EmploymentStatus.previouslyWorked)}
            />
            <RadioTile
              title={`I never worked at ${employerName}`}
              onClick={handleClick(EmploymentStatus.neverWorked)}
              selected={employmentStatus === EmploymentStatus.neverWorked}
            />
          </FlexContainer>
        </Container>
      </CTAFoldContent>
      <CTAFoldCTA>
        <div className="center-text">
          <Button name="continue" text="Continue" onClick={handleContinue} disabled={employmentStatus === null} />
        </div>
      </CTAFoldCTA>
    </CTAFold>
  );
};

export type WaitListNotificationProps = {
  accountDetails: { email: string; cellNumber: string };
  employerName: string;
  fieldValidations: FieldValidationsType;
  updateAccountDetail: (field: "email" | "cellNumber") => (val: any) => void;
  isNotificationSent: boolean;
  handleContinue: () => any;
};

const WaitListNotification = ({
  accountDetails,
  employerName,
  fieldValidations,
  updateAccountDetail,
  isNotificationSent,
  handleContinue,
}: WaitListNotificationProps) => {
  return (
    <Container>
      <Container className="image">
        <Image src={logo} alt="Manifest Logo" />
        <>
          <Container className="header-section-b">
            <Container className="header-section-a">
              <h3>We’re about to launch!</h3>
              <Line />
              <Container className="body-section-b">
                <p className={`medium ${ColorClassNames.coco9}`}>
                  This week, Manifest is only available to current {employerName} employees. However, that might change
                  next week! <br />
                  <br /> Please share your contact information so we can get your accounts consolidated as soon as
                  possible.
                </p>

                <Input
                  name="email"
                  error={!!fieldValidations.errorMessages.email}
                  caption={fieldValidations.errorMessages.email}
                  captionColor={ColorVariables.cherry}
                  handleBlur={() => fieldValidations.validateField("email")}
                  onChange={updateAccountDetail("email")}
                  title="Email"
                  value={accountDetails.email}
                  autoComplete="email"
                />

                <Input
                  name="phone"
                  type="phone"
                  caption={fieldValidations.errorMessages.cellNumber}
                  error={!!fieldValidations.errorMessages.cellNumber}
                  handleBlur={() => fieldValidations.validateField("cellNumber")}
                  onChange={updateAccountDetail("cellNumber")}
                  title="Mobile phone"
                  value={accountDetails.cellNumber}
                  autoComplete="tel"
                />
              </Container>
            </Container>
          </Container>

          <Spacing $space={80} />
          <>
            <Button name="continue" text="Get notified" onClick={handleContinue} />
            <Spacing $space={20} />
            <p className={`center-text xsmall ${ColorClassNames.coco9}`}>
              {isNotificationSent ? "You’re on the list!" : "We’ll notify you once Manifest is available to you."}
            </p>
          </>
          <Spacing $space={160} />
        </>
      </Container>
    </Container>
  );
};

type ConfirmEmployerScreenProps = {
  goToAccountCreationScreen(isCurrentEmployer: boolean): any;
  employerName: string;
  goBackHome: () => void;
};

const ConfirmEmployerScreen = ({ employerName, goToAccountCreationScreen, goBackHome }: ConfirmEmployerScreenProps) => {
  const navigate = useTypedNavigate();
  const [isNotificationSent, setIsNotificationSent] = useState(false);
  const [showMailingScreen, setShowMailingScreen] = useState(false);
  const [accountDetails, setAccountDetails] = useState({
    email: "",
    cellNumber: "",
  });

  const showMailingListScreen = () => setShowMailingScreen(true);

  const handleContinue = async () => {
    const validated = fieldValidations.validate("all");
    if (validated) {
      try {
        await API.user.waitListSignUp(accountDetails.email, accountDetails.cellNumber, employerName);
        return setIsNotificationSent(true);
      } catch (error) {
        navigate(route(RoutePaths.error));
      }
    }
  };

  const fieldValidations = createFieldValidations(
    {
      ...accountDetails,
    },
    {
      email: {
        deps: ["email"],
        validation: () => {
          const regexpEmail =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          const valid = accountDetails.email.length > 0 && regexpEmail.test(accountDetails.email);
          if (!valid) {
            return "Please enter a valid email in format: example@email.com";
          }
        },
      },
      cellNumber: {
        deps: ["cellNumber"],
        validation: () => {
          const valid = validatePhoneNumber(accountDetails.cellNumber);
          if (!valid) {
            return "Please enter a valid 10-digit phone number.";
          }
        },
      },
    }
  );

  const updateAccountDetail = (field: keyof typeof accountDetails) => (val: any) => {
    setAccountDetails({
      ...accountDetails,
      [field]: val,
    });
  };

  return (
    <Container className="full-width">
      {showMailingScreen && (
        <WaitListNotification
          accountDetails={accountDetails}
          employerName={employerName}
          fieldValidations={fieldValidations as any}
          updateAccountDetail={updateAccountDetail}
          isNotificationSent={isNotificationSent}
          handleContinue={handleContinue}
        />
      )}

      {!showMailingScreen &&
        (employerName === "PEHP" ? (
          <PEHPPreAccountCreationScreen
            employerName={employerName}
            goToAccountCreationScreen={goToAccountCreationScreen}
            showMailingListScreen={showMailingListScreen}
            goBackHome={goBackHome}
          />
        ) : (
          <EmployerOptions
            employerName={employerName}
            goToAccountCreationScreen={goToAccountCreationScreen}
            showMailingListScreen={showMailingListScreen}
          />
        ))}
    </Container>
  );
};

export default ConfirmEmployerScreen;
